/**
 * Session-scoped cache for dual-model comparison drafts.
 *
 * Compare mode never writes fallback-provider paragraphs to Firestore (so
 * approvals and the stored Gemini draft stay untouched). The clinician still
 * needs those drafts after a refresh, so they live in sessionStorage for
 * this browser tab only.
 *
 * The payload is clinical text (PII). Like dashboard filters and Autism
 * History notes, the whole namespace is wiped on sign-out / token expiry
 * via clearCompareOutputsCache() so a later clinician on a shared tab
 * cannot see the previous patient's comparison drafts.
 *
 * Bump the version segment if the persisted shape changes incompatibly.
 */

export const COMPARE_PREFIX = 'td.compare.v1.';

/**
 * @param {string} patientId
 * @param {string} reportStage - e.g. "Clinical Interview", "Semble MDT Report"
 * @returns {string}
 */
export function compareOutputsKey(patientId, reportStage) {
  return `${COMPARE_PREFIX}${patientId}.${reportStage}`;
}

/** Empty cache payload used as the useSessionState initial value. */
export const EMPTY_COMPARE_CACHE = Object.freeze({
  outputs: null,
  showClaude: false,
  sideBySide: false,
});

/**
 * Merge a patch onto a compare-cache payload without dropping fields.
 * Older session entries may omit sideBySide; treat a missing flag as off.
 *
 * @param {Object|null} prev
 * @param {Object} patch
 * @returns {{outputs: Object|null, showClaude: boolean, sideBySide: boolean}}
 */
export function mergeCompareCache(prev, patch) {
  return {
    outputs: prev?.outputs || null,
    showClaude: Boolean(prev?.showClaude),
    sideBySide: Boolean(prev?.sideBySide),
    ...patch,
  };
}

/**
 * Remove every compare-mode cache entry in this tab.
 * Safe to call when sessionStorage is unavailable.
 */
export function clearCompareOutputsCache() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(COMPARE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {
    // sessionStorage unavailable — nothing to clear.
  }
}
